import { adminJson } from './adminJson';
import { adminRequest, adminList } from './adminApi';

export type SettingsMap = Record<string, string | null>;

export function fetchSettingKeys(keys: string[]) {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/keys?keys=${encodeURIComponent(keys.join(','))}`);
}

export function fetchBusinessSetup() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/business-setup`);
}

export function fetchOrderSettings() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/order`);
}

export function fetchPaymentSettings() {
  return adminJson<{
    data: {
      business: SettingsMap;
      gateways: Array<{
        gateway: string;
        mode: string;
        status: number;
        values: Record<string, unknown>;
        additional_data: Record<string, unknown>;
      }>;
    };
  }>(`/admin/json-compat/settings/payment`);
}

export function fetchMailSettings() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/mail`);
}

export function fetchFcmSettings() {
  return adminJson<{
    data: { credentials: SettingsMap; push_notification_service_file_content: string | null };
  }>(`/admin/json-compat/settings/fcm`);
}

export function fetchSocialLogin() {
  return adminJson<{ data: { google: SettingsMap; apple: SettingsMap; google_login: SettingsMap } }>(
    `/admin/json-compat/settings/social-login`,
  );
}

export function fetchRecaptcha() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/recaptcha`);
}

export function fetchAnalytics() {
  return adminJson<{
    data: Array<{ type: string; status: number; script_id: string | null; raw: Record<string, unknown> }>;
  }>(`/admin/json-compat/settings/analytics`);
}

export function fetchNotificationSettings() {
  return adminJson<{
    data: Array<{ id: number; key: string; module_type: string; message: string; status: number }>;
  }>(`/admin/json-compat/settings/notification`);
}

export function fetchOfflinePayments() {
  return adminList<{
    id: number | string;
    name: string;
    status: number;
    additional_data?: unknown;
  }>(`/json-compat/settings/offline-payments`);
}

export function fetchSocialMedia() {
  return adminList<{ id: number | string; name: string; link: string; status: number }>(
    `/json-compat/settings/social-media`,
  );
}

export function fetchLanguages() {
  return adminJson<{
    data: {
      languages: Array<{
        id: number | string;
        code: string;
        direction: string;
        status: number;
        default?: boolean;
      }>;
      language_codes: string[] | null;
    };
  }>(`/admin/json-compat/settings/languages`);
}

export function fetchLanguageKeys(code: string) {
  return adminJson<{ data: Record<string, string>; count: number }>(
    `/admin/json-compat/settings/language-keys/${encodeURIComponent(code)}`,
  );
}

export function fetchPolicies() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/policies`);
}

export function fetchEmailTemplates() {
  return adminJson<{
    data: Array<{
      id: number;
      type: string;
      email_type: string;
      title: string | null;
      body: string | null;
      body_2: string | null;
      button_name: string | null;
      button_url: string | null;
      footer_text: string | null;
      copyright_text: string | null;
      email_template: string | null;
      background_image: string | null;
      image: string | null;
      logo: string | null;
      icon: string | null;
    }>;
  }>(`/admin/json-compat/settings/email-templates`);
}

export function fetchEmailTemplate(type: string, emailType: string) {
  return adminJson<{ data: Record<string, unknown> | null }>(
    `/admin/json-compat/settings/email-template/${encodeURIComponent(type)}/${encodeURIComponent(emailType)}`,
  );
}

export function fetchLanding() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/landing`);
}

export function fetchZones() {
  return adminList<{
    id: number | string;
    name: string;
    status: number;
    stores_count: number;
    deliverymen_count: number;
  }>(`/json-compat/settings/zones`);
}

export function fetchSurgePrices(zoneId?: string | number) {
  return adminList<{
    id: number | string;
    zone_id: number | string;
    surge_price_name: string;
    price: number | string;
    price_type: string;
    duration_type: string;
    status: number;
    customer_note?: string | null;
  }>(`/json-compat/settings/surge-prices`, { zone_id: zoneId });
}

export function fetchSubscriptions() {
  return adminJson<{
    data: {
      packages: Array<{
        id: number | string;
        package_name: string;
        price: number | string;
        validity: number | string;
        validity_type: string;
        status: number;
        module_type: string;
        current_subscribers_count: number;
        description?: string | null;
      }>;
      subscribers: Array<{
        id: number | string;
        store_id: number | string;
        store_name: string | null;
        package_name: string | null;
        status: number;
        start_date?: string | null;
        end_date?: string | null;
      }>;
      settings: SettingsMap;
    };
  }>(`/admin/json-compat/settings/subscriptions`);
}

export function fetchModules() {
  return adminList<{
    id: number | string;
    module_name: string;
    module_type: string;
    status: number;
    stores_count: number;
  }>(`/json-compat/settings/modules`);
}

export function fetchFileManager(folder = 'cHVibGlj', storage = 'local', page = 1) {
  return adminJson<{
    data: Array<{ name?: string; path?: string; type?: string; size?: string | number; created_at?: string }>;
    meta?: { current_page: number; last_page: number; per_page: number; total: number };
    folder_path: string;
    storage: string;
  }>(`/admin/json-compat/settings/file-manager?folder=${encodeURIComponent(folder)}&storage=${encodeURIComponent(storage)}&page=${page}`);
}

export function fetchSystemAddons() {
  return adminList<{ name: string; path: string; status: number }>(`/json-compat/settings/system-addons`);
}

export function fetchAppSettings() {
  return adminJson<{ data: SettingsMap }>(`/admin/json-compat/settings/app`);
}

export function fetchIntegrations() {
  return adminJson<{ data: { settings: SettingsMap; mail_config: SettingsMap | null } }>(
    `/admin/json-compat/settings/integrations`,
  );
}

export function fetchEntityDetail(type: string, id: string | number) {
  return adminRequest<import('@/types/entityDetail').EntityDetail>(
    `/entity-detail/${encodeURIComponent(type)}/${id}`,
    { method: 'GET' },
  );
}

export function saveFormData(path: string, form: FormData) {
  return adminRequest<{ success?: string; message?: string }>(path, { method: 'POST', body: form });
}

export function saveJson(path: string, body: Record<string, unknown>, method: 'POST' | 'PUT' | 'GET' = 'POST') {
  return adminRequest<{ success?: string; message?: string }>(path, { method, body });
}
